//@flow
import React from 'react';
import { themr } from 'react-css-themr';
import { Checkbox, CheckboxTypes } from 'common/components/core/checkbox';
import classnames from 'classnames';
import { compose } from 'recompose';
import { connect } from 'react-redux';
import { translate } from 'react-i18next';
import currencyToSymbolMap from 'currency-symbol-map/map';
import { Link } from 'react-router-dom';

import OrderActions from 'common/redux/actions/orderMaster';
import ToastActions from 'common/redux/actions/toast';
import AppPreferencesActions from 'common/redux/actions/appPreferences';

import {
    clientSelector,
    isExportEntityActive as isExportEntityActiveSelector
} from 'common/redux/selectors/queryParamsSelectors';
import OrderMasterSelectors from 'common/redux/selectors/orderMaster';
import { teamsSelector } from 'common/redux/selectors/teamSelector';
import { getOrderManagementSettings } from 'common/redux/selectors/clientSelector';
import { getSectionVisibilitySettings } from 'common/redux/selectors/personnelSelectors';
import { getEntityTablePreferences } from 'common/redux/selectors/appPreferences';
import {
    OrderSubStatusEnum,
    OrderStatusEnum,
    TemperatureTypeEnum,
    TemperatureUnitEnum,
    ExchangeTypeEnum,
    WeightUnitEnum,
    VolumeUnitEnum,
    QuantityUnitEnum,
    FulfilledByEnum
} from 'common/models/internal/MasterOrder';

import {
    CellDataTypes,
    FROZEN_COLUMN_LIMIT
} from 'common/components/core/table';
import { withRouter } from 'common/components/core/router';

import { ConnectedTeamIdSelectorFilter } from 'common/entity/components/TeamSelectorFilter';

import { OrderFilter } from 'common/entity/orderMaster/OrderSelectorFilter';
import DateTimeRangeFilter from 'common/entity/orderMaster/DateTimeRangeFilter';
import CreateBatchModal from 'common/entity/orderMaster/CreateBatchModal';
import FuelTypeFilter from 'common/entity/components/FuelTypeFilter';
import { entityMasterFactory } from 'common/entity/generic/MasterContainer';
import MasterContainerHOC from 'common/entity/generic/MasterContainerHOC';
import { dashboardColors } from 'common/enums/ColorEnum';

import theme from 'common/entity/BulkEditContainer.scss';

import VisitUtils from 'common/utils/VisitUtils';
import { DataUtils } from 'common/utils/DataUtils';

import { isGlobalPersonnelSelector } from 'common/redux/selectors/sessionSelector';

import { FilterBuilder, FilterOperation } from 'common/models/internal/Filter';
import FilterListBuilder from 'common/models/helpers/FilterListBuilder';

import type { RouterHistory } from 'react-router-dom';
import type { Location } from 'common/components/core/router';
import type { Team } from 'common/models/internal/Team';
import type { Map } from 'immutable';
import type { OrderManagementSettings } from 'common/models/internal/OrderManagementSettings';
import type { ToastType } from 'common/components/core/toast/Toast';
import type { EntityColumnType } from 'common/entity/generic/MasterContainer';
import type { OrdersDataType } from 'common/redux/selectors/orderMaster';

import MasterExportContainer from 'common/entity/generic/MasterExportContainer';
import {
    ENTITY_TABLE_ID,
    ENTITY_NAME_ID,
    ENTITY_EXPORT_ID
} from 'common/constants/entity/orderMaster';
import { SectionVisibilitySettings } from 'common/models/internal/Client';
import OrderMasterExportHandler from './OrderMasterExportHandler';

const getTeamIdFilter = ({ filteredColumn }) => {
    const {
        value: { allowEmptyOrNull, values }
    } = filteredColumn;
    return new FilterBuilder()
        .withName('teamId')
        .withOperation(FilterOperation.Equals)
        .withValues(values)
        .withAllowEmptyOrNull(allowEmptyOrNull)
        .withCaseSensitive(true);
};

const COLUMNS: Array<EntityColumnType> = [
    {
        id: 'selector',
        Cell: props => {
            const {
                tdProps: {
                    rest: { isChecked, onCheckboxClick, theme }
                }
            } = props;
            return (
                <Checkbox
                    checkboxType={CheckboxTypes.Solid}
                    checkboxColor={dashboardColors.darkSkyBlue.name}
                    theme={theme}
                    className={theme.selectionCell}
                    checked={isChecked}
                    onChange={onCheckboxClick}
                />
            );
        },
        sortable: false,
        filterable: false,
        frozen: true,
        disablePinControl: true,
        width: 40
    },
    {
        headerTextId: 'orderMasterTable.id',
        id: 'id',
        accessor: 'id',
        minWidth: 150,
        FilterComponent: OrderFilter,
        getCustomFilterInfo: ({ filteredColumn }) => {
            const {
                value: { allowEmptyOrNull, values }
            } = filteredColumn;
            return new FilterBuilder()
                .withName('id')
                .withOperation(FilterOperation.Equals)
                .withValues(values)
                .withAllowEmptyOrNull(allowEmptyOrNull)
                .withCaseSensitive(true);
        }
    },
    {
        headerTextId: 'orderMasterTable.alternateId',
        id: 'sourceOrderId',
        accessor: 'sourceOrderId',
        minWidth: 120,
        infixSearchable: true
    },
    {
        headerTextId: 'orderMasterTable.name',
        id: 'name',
        accessor: 'name',
        minWidth: 100,
        infixSearchable: true
    },
    {
        headerTextId: 'orderMasterTable.code',
        id: 'code',
        accessor: 'code',
        minWidth: 100,
        infixSearchable: true
    },
    {
        headerTextId: 'orderMasterTable.orderStatus',
        id: 'effectiveStatus',
        accessor: 'effectiveStatus',
        minWidth: 100,
        FilterComponent: FuelTypeFilter,
        getCustomFilterInfo: ({ filteredColumn }) => {
            const {
                value: { allowEmptyOrNull, values }
            } = filteredColumn;
            return new FilterBuilder()
                .withName('effectiveStatus')
                .withOperation(FilterOperation.Equals)
                .withValues(values)
                .withAllowEmptyOrNull(allowEmptyOrNull);
        },
        getFilterOptions: () => {
            const options = OrderStatusEnum.getOptions({
                translationKey: 'masterOrderProperties.orderStatuses'
            });
            return options;
        },
        Cell: ({
            original,
            tdProps: {
                rest: { t }
            }
        }) => {
            return t(
                `entityManagement:masterOrderProperties:orderStatuses.${original.effectiveStatus}`
            );
        }
    },
    {
        headerTextId: 'orderMasterTable.orderSubStatus',
        id: 'orderStatus',
        accessor: 'orderStatus',
        minWidth: 100,
        FilterComponent: FuelTypeFilter,
        getCustomFilterInfo: ({ filteredColumn }) => {
            const {
                value: { allowEmptyOrNull, values }
            } = filteredColumn;
            return new FilterBuilder()
                .withName('orderStatus')
                .withOperation(FilterOperation.Equals)
                .withValues(values)
                .withAllowEmptyOrNull(allowEmptyOrNull);
        },
        getFilterOptions: () => {
            const options = OrderSubStatusEnum.getOptions({
                translationKey: 'masterOrderProperties.orderSubStatuses'
            });
            return options;
        },
        Cell: ({
            original,
            tdProps: {
                rest: { t }
            }
        }) => {
            return t(
                `entityManagement:masterOrderProperties:orderSubStatuses.${original.orderStatus}`
            );
        }
    },
    {
        headerTextId: 'orderMasterTable.teamId',
        id: 'teamId',
        accessor: 'teamId',
        infixSearchable: true,
        sortable: true,
        minWidth: 200,
        getExtraFilterProps: () => ({
            isMulti: false
        }),
        FilterComponent: ConnectedTeamIdSelectorFilter,
        getCustomFilterInfo: getTeamIdFilter
    },
    {
        headerTextId: 'orderMasterTable.fulfilledBy',
        id: 'usecase',
        accessor: 'usecase',
        minWidth: 100,
        FilterComponent: FuelTypeFilter,
        getCustomFilterInfo: ({ filteredColumn }) => {
            const {
                value: { allowEmptyOrNull, values }
            } = filteredColumn;
            return new FilterBuilder()
                .withName('usecase')
                .withOperation(FilterOperation.Equals)
                .withValues(values)
                .withAllowEmptyOrNull(allowEmptyOrNull);
        },
        getFilterOptions: () => {
            const options = FulfilledByEnum.getOptions({
                translationKey: 'masterOrderProperties.fulfilledByType'
            });
            return options;
        },
        Cell: ({
            original,
            tdProps: {
                rest: { t }
            }
        }) => {
            if (original.usecase) {
                return t(
                    `entityManagement:masterOrderProperties.fulfilledByType.${original.usecase}`
                );
            }
            return '-';
        }
    },
    {
        headerTextId: 'orderMasterTable.skills',
        id: 'skills',
        accessor: 'skills',
        minWidth: 100,
        infixSearchable: true,
        Cell: props => {
            const { original } = props;
            const skillIds = original.skills?.join(', ');
            return skillIds;
        }
    },
    {
        headerTextId: 'orderMasterTable.totalQuantity',
        id: 'quantity',
        accessor: 'quantity.value',
        Cell: props => {
            const { original } = props;
            const value = original.getIn(['quantity', 'value']);
            if (DataUtils.isEmpty(value)) {
                return '-';
            }
            return value;
        },
        getCustomFilterInfo: ({ filteredColumn }) => {
            const { value } = filteredColumn;
            return new FilterBuilder()
                .withName('quantity.value')
                .withOperation(FilterOperation.Equals)
                .withValue(value);
        }
    },
    {
        headerTextId: 'orderMasterTable.totalQuantityUnit',
        id: 'quantityUnit',
        accessor: 'quantity.unit',
        FilterComponent: FuelTypeFilter,
        Cell: ({
            original,
            tdProps: {
                rest: { t }
            }
        }) => {
            if (original.quantity?.unit) {
                return t(
                    `entityManagement:masterOrderProperties.quantityUnits.${original.quantity.unit}`
                );
            }
            return '-';
        },
        getCustomFilterInfo: ({ filteredColumn }) => {
            const {
                value: { allowEmptyOrNull, values }
            } = filteredColumn;
            return new FilterBuilder()
                .withName('quantity.unit')
                .withOperation(FilterOperation.Equals)
                .withValues(values)
                .withAllowEmptyOrNull(allowEmptyOrNull);
        },
        getFilterOptions: () => {
            const options = QuantityUnitEnum.getOptions({
                translationKey: 'masterOrderProperties.quantityUnits'
            });
            return options;
        }
    },
    {
        headerTextId: 'orderMasterTable.temperatureLowerThreshold',
        id: 'temperatureLowerThreshold',
        accessor: 'temperatureThreshold.lowerThreshold',
        minWidth: 100,
        infixSearchable: true,
        getCustomFilterInfo: ({ filteredColumn }) => {
            const { value } = filteredColumn;
            return new FilterBuilder()
                .withName('temperatureThreshold.lowerThreshold')
                .withOperation(FilterOperation.Equals)
                .withValue(value);
        }
    },
    {
        headerTextId: 'orderMasterTable.temperatureHigherThreshold',
        id: 'temperatureHigherThreshold',
        accessor: 'temperatureThreshold.higherThreshold',
        minWidth: 100,
        infixSearchable: true,
        getCustomFilterInfo: ({ filteredColumn }) => {
            const { value } = filteredColumn;
            return new FilterBuilder()
                .withName('temperatureThreshold.higherThreshold')
                .withOperation(FilterOperation.Equals)
                .withValue(value);
        }
    },
    {
        headerTextId: 'orderMasterTable.temperatureType',
        id: 'temperatureType',
        accessor: 'temperatureThreshold.temperatureType',
        minWidth: 100,
        FilterComponent: FuelTypeFilter,
        getCustomFilterInfo: ({ filteredColumn }) => {
            const {
                value: { allowEmptyOrNull, values }
            } = filteredColumn;
            return new FilterBuilder()
                .withName('temperatureThreshold.temperatureType')
                .withOperation(FilterOperation.Equals)
                .withValues(values)
                .withAllowEmptyOrNull(allowEmptyOrNull);
        },
        getFilterOptions: () => {
            const options = TemperatureTypeEnum.getOptions({
                translationKey: 'masterOrderProperties.temperatureTypes'
            });
            return options;
        }
    },
    {
        headerTextId: 'orderMasterTable.temperatureUnit',
        id: 'temperatureUnit',
        accessor: 'temperatureThreshold.unit',
        minWidth: 100,
        FilterComponent: FuelTypeFilter,
        getCustomFilterInfo: ({ filteredColumn }) => {
            const {
                value: { allowEmptyOrNull, values }
            } = filteredColumn;
            return new FilterBuilder()
                .withName('temperatureThreshold.unit')
                .withOperation(FilterOperation.Equals)
                .withValues(values)
                .withAllowEmptyOrNull(allowEmptyOrNull);
        },
        getFilterOptions: () => {
            const options = TemperatureUnitEnum.getOptions({
                translationKey: 'masterOrderProperties.temperatureUnits'
            });
            return options;
        }
    },
    {
        headerTextId: 'orderMasterTable.shiftId',
        id: 'shiftId',
        accessor: 'shiftId',
        minWidth: 100,
        infixSearchable: true
    },
    {
        headerTextId: 'orderMasterTable.scanId',
        id: 'scanId',
        accessor: 'scanId',
        minWidth: 100,
        infixSearchable: true
    },
    {
        headerTextId: 'orderMasterTable.pickupVisitName',
        id: 'pickupVisitName',
        accessor: 'pickupVisitName',
        minWidth: 100,
        infixSearchable: true
    },
    {
        headerTextId: 'orderMasterTable.pickupLocationId',
        id: 'pickupLocationId',
        accessor: 'pickupLocationId',
        minWidth: 100,
        infixSearchable: true
    },
    {
        headerTextId: 'orderMasterTable.pickupContactPointName',
        id: 'pickupContactPointName',
        accessor: 'pickupContactPoint.name',
        minWidth: 100,
        infixSearchable: true
    },
    {
        headerTextId: 'orderMasterTable.pickupContactPointNumber',
        id: 'pickupContactPointNumber',
        accessor: 'pickupContactPoint.number',
        minWidth: 100,
        infixSearchable: true
    },
    {
        headerTextId: 'orderMasterTable.pickupLocationAddress.placeName',
        id: 'pickupLocationAddress.placeName',
        accessor: 'pickupLocationAddress.placeName',
        minWidth: 100,
        infixSearchable: true
    },
    {
        headerTextId: 'orderMasterTable.pickupLocationAddress.localityName',
        id: 'pickupLocationAddress.localityName',
        accessor: 'pickupLocationAddress.localityName',
        minWidth: 100,
        infixSearchable: true
    },
    {
        headerTextId: 'orderMasterTable.pickupLocationAddress.formattedAddress',
        id: 'pickupLocationAddress.formattedAddress',
        accessor: 'pickupLocationAddress.formattedAddress',
        minWidth: 100,
        infixSearchable: true
    },
    {
        headerTextId: 'orderMasterTable.pickupLocationAddress.subLocalityName',
        id: 'pickupLocationAddress.subLocalityName',
        accessor: 'pickupLocationAddress.subLocalityName',
        minWidth: 100,
        infixSearchable: true
    },
    {
        headerTextId: 'orderMasterTable.pickupLocationAddress.pincode',
        id: 'pickupLocationAddress.pincode',
        accessor: 'pickupLocationAddress.pincode',
        minWidth: 100,
        infixSearchable: true
    },
    {
        headerTextId: 'orderMasterTable.pickupLocationAddress.city',
        id: 'pickupLocationAddress.city',
        accessor: 'pickupLocationAddress.city',
        minWidth: 100,
        infixSearchable: true
    },
    {
        headerTextId: 'orderMasterTable.pickupLocationAddress.state',
        id: 'pickupLocationAddress.state',
        accessor: 'pickupLocationAddress.state',
        minWidth: 100,
        infixSearchable: true
    },
    {
        headerTextId: 'orderMasterTable.pickupLocationAddress.countryCode',
        id: 'pickupLocationAddress.countryCode',
        accessor: 'pickupLocationAddress.countryCode',
        minWidth: 100,
        infixSearchable: true
    },
    {
        headerTextId: 'orderMasterTable.pickupLocationAddress.locationType',
        id: 'pickupLocationAddress.locationType',
        accessor: 'pickupLocationAddress.locationType',
        minWidth: 100,
        infixSearchable: true
    },
    {
        headerTextId: 'orderMasterTable.pickupLat',
        id: 'pickupLat',
        accessor: 'pickupLatLng.lat',
        minWidth: 100,
        infixSearchable: true
    },
    {
        headerTextId: 'orderMasterTable.pickupLng',
        id: 'pickupLng',
        accessor: 'pickupLatLng.lng',
        minWidth: 100,
        infixSearchable: true
    },
    {
        headerTextId: 'orderMasterTable.pickupDate',
        id: 'pickupDate',
        accessor: 'pickupDate',
        minWidth: 150,
        FilterComponent: DateTimeRangeFilter,
        getExtraFilterProps: () => ({
            id: 'pickupDate',
            showTime: false
        }),
        getCustomFilterInfo: ({ filteredColumn }) => {
            const {
                value: { min, max }
            } = filteredColumn;
            const rangeFilter = [];
            if (min) {
                rangeFilter.push(
                    new FilterBuilder()
                        .withName('pickupDate')
                        .withOperation(FilterOperation.GreaterThanOrEqualTo)
                        .withValue(min)
                );
            }
            if (max) {
                rangeFilter.push(
                    new FilterBuilder()
                        .withName('pickupDate')
                        .withOperation(FilterOperation.LesserThanOrEqualTo)
                        .withValue(max)
                );
            }
            return rangeFilter;
        }
    },
    {
        headerTextId: 'orderMasterTable.pickupSlotStart',
        id: 'pickupSlotStart',
        accessor: 'pickupSlot.start',
        dataType: CellDataTypes.DateTime,
        minWidth: 150,
        FilterComponent: DateTimeRangeFilter,
        getExtraFilterProps: () => ({
            id: 'pickupSlotStart',
            showTime: true
        }),
        getCustomFilterInfo: ({ filteredColumn }) => {
            const {
                value: { min, max }
            } = filteredColumn;
            const rangeFilter = [];
            if (min) {
                rangeFilter.push(
                    new FilterBuilder()
                        .withName('pickupSlot.start')
                        .withOperation(FilterOperation.GreaterThanOrEqualTo)
                        .withValue(min)
                );
            }
            if (max) {
                rangeFilter.push(
                    new FilterBuilder()
                        .withName('pickupSlot.start')
                        .withOperation(FilterOperation.LesserThanOrEqualTo)
                        .withValue(max)
                );
            }
            return rangeFilter;
        }
    },
    {
        headerTextId: 'orderMasterTable.pickupSlotEnd',
        id: 'pickupSlotEnd',
        accessor: 'pickupSlot.end',
        dataType: CellDataTypes.DateTime,
        minWidth: 150,
        FilterComponent: DateTimeRangeFilter,
        getExtraFilterProps: () => ({
            id: 'pickupSlotEnd',
            showTime: true
        }),
        getCustomFilterInfo: ({ filteredColumn }) => {
            const {
                value: { min, max }
            } = filteredColumn;
            const rangeFilter = [];
            if (min) {
                rangeFilter.push(
                    new FilterBuilder()
                        .withName('pickupSlot.end')
                        .withOperation(FilterOperation.GreaterThanOrEqualTo)
                        .withValue(min)
                );
            }
            if (max) {
                rangeFilter.push(
                    new FilterBuilder()
                        .withName('pickupSlot.end')
                        .withOperation(FilterOperation.LesserThanOrEqualTo)
                        .withValue(max)
                );
            }
            return rangeFilter;
        }
    },
    {
        headerTextId: 'orderMasterTable.pickupTransactionDuration',
        id: 'pickupTransactionDuration',
        accessor: 'pickupTransactionDuration',
        minWidth: 100,
        infixSearchable: true,
        dataType: CellDataTypes.HumanizedTime,
        timeUnits: ['h', 'm', 's']
    },
    {
        headerTextId: 'orderMasterTable.pickupAmount',
        id: 'pickupAmount',
        accessor: 'pickupAmount.amount.amount',
        minWidth: 100,
        infixSearchable: true,
        getCustomFilterInfo: ({ filteredColumn }) => {
            const { value } = filteredColumn;
            return new FilterBuilder()
                .withName('pickupAmount.amount.amount')
                .withOperation(FilterOperation.Equals)
                .withValue(value);
        }
    },
    {
        headerTextId: 'orderMasterTable.pickupCurrency',
        id: 'pickupAmountCurrency',
        accessor: 'pickupAmount.amount.currency',
        minWidth: 100,
        FilterComponent: FuelTypeFilter,
        getCustomFilterInfo: ({ filteredColumn }) => {
            const {
                value: { allowEmptyOrNull, values }
            } = filteredColumn;
            return new FilterBuilder()
                .withName('pickupAmount.amount.currency')
                .withOperation(FilterOperation.Equals)
                .withValues(values)
                .withAllowEmptyOrNull(allowEmptyOrNull);
        },
        getFilterOptions: () => {
            const options = Object.entries(currencyToSymbolMap).map(
                ([currency]) => ({
                    label: currency,
                    value: currency
                })
            );
            return options;
        }
    },
    {
        headerTextId: 'orderMasterTable.pickupSymbol',
        id: 'pickupAmountSymbol',
        accessor: 'pickupAmount.amount.symbol',
        minWidth: 100,
        filterable: false
    },
    {
        headerTextId: 'orderMasterTable.pickupExchangeType',
        id: 'pickupAmountExchange',
        accessor: 'pickupAmount.exchangeType',
        minWidth: 100,
        FilterComponent: FuelTypeFilter,
        getCustomFilterInfo: ({ filteredColumn }) => {
            const {
                value: { allowEmptyOrNull, values }
            } = filteredColumn;
            return new FilterBuilder()
                .withName('pickupAmount.exchangeType')
                .withOperation(FilterOperation.Equals)
                .withValues(values)
                .withAllowEmptyOrNull(allowEmptyOrNull);
        },
        getFilterOptions: () => {
            const options = ExchangeTypeEnum.getOptions({
                translationKey: 'masterOrderProperties.ExchangeTypes'
            });
            return options;
        }
    },
    {
        headerTextId: 'orderMasterTable.pickupCustomerId',
        id: 'pickupCustomerId',
        accessor: 'pickupCustomerId',
        minWidth: 100,
        infixSearchable: true
    },
    {
        headerTextId: 'orderMasterTable.pickupAddressId',
        id: 'pickupAddressId',
        accessor: 'pickupAddressId',
        minWidth: 100,
        infixSearchable: true
    },
    {
        headerTextId: 'orderMasterTable.dropVisitName',
        id: 'dropVisitName',
        accessor: 'dropVisitName',
        minWidth: 100,
        infixSearchable: true
    },
    {
        headerTextId: 'orderMasterTable.dropLocationId',
        id: 'dropLocationId',
        accessor: 'dropLocationId',
        minWidth: 100,
        infixSearchable: true
    },
    {
        headerTextId: 'orderMasterTable.dropContactPointName',
        id: 'dropContactPointName',
        accessor: 'dropContactPoint.name',
        minWidth: 100,
        infixSearchable: true
    },
    {
        headerTextId: 'orderMasterTable.dropContactPointNumber',
        id: 'dropContactPointNumber',
        accessor: 'dropContactPoint.number',
        minWidth: 100,
        infixSearchable: true
    },
    {
        headerTextId: 'orderMasterTable.dropLocationAddress.placeName',
        id: 'dropLocationAddress.placeName',
        accessor: 'dropLocationAddress.placeName',
        minWidth: 100,
        infixSearchable: true
    },
    {
        headerTextId: 'orderMasterTable.dropLocationAddress.localityName',
        id: 'dropLocationAddress.localityName',
        accessor: 'dropLocationAddress.localityName',
        minWidth: 100,
        infixSearchable: true
    },
    {
        headerTextId: 'orderMasterTable.dropLocationAddress.formattedAddress',
        id: 'dropLocationAddress.formattedAddress',
        accessor: 'dropLocationAddress.formattedAddress',
        minWidth: 100,
        infixSearchable: true
    },
    {
        headerTextId: 'orderMasterTable.dropLocationAddress.subLocalityName',
        id: 'dropLocationAddress.subLocalityName',
        accessor: 'dropLocationAddress.subLocalityName',
        minWidth: 100,
        infixSearchable: true
    },
    {
        headerTextId: 'orderMasterTable.dropLocationAddress.pincode',
        id: 'dropLocationAddress.pincode',
        accessor: 'dropLocationAddress.pincode',
        minWidth: 100,
        infixSearchable: true
    },
    {
        headerTextId: 'orderMasterTable.dropLocationAddress.city',
        id: 'dropLocationAddress.city',
        accessor: 'dropLocationAddress.city',
        minWidth: 100,
        infixSearchable: true
    },
    {
        headerTextId: 'orderMasterTable.dropLocationAddress.state',
        id: 'dropLocationAddress.state',
        accessor: 'dropLocationAddress.state',
        minWidth: 100,
        infixSearchable: true
    },
    {
        headerTextId: 'orderMasterTable.dropLocationAddress.countryCode',
        id: 'dropLocationAddress.countryCode',
        accessor: 'dropLocationAddress.countryCode',
        minWidth: 100,
        infixSearchable: true
    },
    {
        headerTextId: 'orderMasterTable.dropLocationAddress.locationType',
        id: 'dropLocationAddress.locationType',
        accessor: 'dropLocationAddress.locationType',
        minWidth: 100,
        infixSearchable: true
    },
    {
        headerTextId: 'orderMasterTable.dropLat',
        id: 'dropLat',
        accessor: 'dropLatLng.lat',
        minWidth: 100,
        infixSearchable: true
    },
    {
        headerTextId: 'orderMasterTable.dropLng',
        id: 'dropLng',
        accessor: 'dropLatLng.lng',
        minWidth: 100,
        infixSearchable: true
    },
    {
        headerTextId: 'orderMasterTable.dropDate',
        id: 'dropDate',
        accessor: 'dropDate',
        minWidth: 150,
        FilterComponent: DateTimeRangeFilter,
        getExtraFilterProps: () => ({
            id: 'dropDate',
            showTime: false
        }),
        getCustomFilterInfo: ({ filteredColumn }) => {
            const {
                value: { min, max }
            } = filteredColumn;
            const rangeFilter = [];
            if (min) {
                rangeFilter.push(
                    new FilterBuilder()
                        .withName('dropDate')
                        .withOperation(FilterOperation.GreaterThanOrEqualTo)
                        .withValue(min)
                );
            }
            if (max) {
                rangeFilter.push(
                    new FilterBuilder()
                        .withName('dropDate')
                        .withOperation(FilterOperation.LesserThanOrEqualTo)
                        .withValue(max)
                );
            }
            return rangeFilter;
        }
    },
    {
        headerTextId: 'orderMasterTable.dropSlotStart',
        id: 'dropSlotStart',
        accessor: 'dropSlot.start',
        dataType: CellDataTypes.DateTime,
        minWidth: 150,
        FilterComponent: DateTimeRangeFilter,
        getExtraFilterProps: () => ({
            id: 'dropSlotStart',
            showTime: true
        }),
        getCustomFilterInfo: ({ filteredColumn }) => {
            const {
                value: { min, max }
            } = filteredColumn;
            const rangeFilter = [];
            if (min) {
                rangeFilter.push(
                    new FilterBuilder()
                        .withName('dropSlot.start')
                        .withOperation(FilterOperation.GreaterThanOrEqualTo)
                        .withValue(min)
                );
            }
            if (max) {
                rangeFilter.push(
                    new FilterBuilder()
                        .withName('dropSlot.start')
                        .withOperation(FilterOperation.LesserThanOrEqualTo)
                        .withValue(max)
                );
            }
            return rangeFilter;
        }
    },
    {
        headerTextId: 'orderMasterTable.dropSlotEnd',
        id: 'dropSlotEnd',
        accessor: 'dropSlot.end',
        dataType: CellDataTypes.DateTime,
        minWidth: 150,
        FilterComponent: DateTimeRangeFilter,
        getExtraFilterProps: () => ({
            id: 'dropSlotEnd',
            showTime: true
        }),
        getCustomFilterInfo: ({ filteredColumn }) => {
            const {
                value: { min, max }
            } = filteredColumn;
            const rangeFilter = [];
            if (min) {
                rangeFilter.push(
                    new FilterBuilder()
                        .withName('dropSlot.end')
                        .withOperation(FilterOperation.GreaterThanOrEqualTo)
                        .withValue(min)
                );
            }
            if (max) {
                rangeFilter.push(
                    new FilterBuilder()
                        .withName('dropSlot.end')
                        .withOperation(FilterOperation.LesserThanOrEqualTo)
                        .withValue(max)
                );
            }
            return rangeFilter;
        }
    },
    {
        headerTextId: 'orderMasterTable.dropTransactionDuration',
        id: 'dropTransactionDuration',
        accessor: 'dropTransactionDuration',
        minWidth: 100,
        infixSearchable: true,
        dataType: CellDataTypes.HumanizedTime,
        timeUnits: ['h', 'm', 's']
    },
    {
        headerTextId: 'orderMasterTable.dropAmount',
        id: 'dropAmount',
        accessor: 'dropAmount.amount.amount',
        minWidth: 100,
        infixSearchable: true,
        getCustomFilterInfo: ({ filteredColumn }) => {
            const { value } = filteredColumn;
            return new FilterBuilder()
                .withName('dropAmount.amount.amount')
                .withOperation(FilterOperation.Equals)
                .withValue(value);
        }
    },
    {
        headerTextId: 'orderMasterTable.dropCurrency',
        id: 'dropAmountCurrency',
        accessor: 'dropAmount.amount.currency',
        minWidth: 100,
        FilterComponent: FuelTypeFilter,
        getCustomFilterInfo: ({ filteredColumn }) => {
            const {
                value: { allowEmptyOrNull, values }
            } = filteredColumn;
            return new FilterBuilder()
                .withName('dropAmount.amount.currency')
                .withOperation(FilterOperation.Equals)
                .withValues(values)
                .withAllowEmptyOrNull(allowEmptyOrNull);
        },
        getFilterOptions: () => {
            const options = Object.entries(currencyToSymbolMap).map(
                ([currency]) => ({
                    label: currency,
                    value: currency
                })
            );
            return options;
        }
    },
    {
        headerTextId: 'orderMasterTable.dropSymbol',
        id: 'dropAmountSymbol',
        accessor: 'dropAmount.amount.symbol',
        minWidth: 100,
        filterable: false
    },
    {
        headerTextId: 'orderMasterTable.dropExchangeType',
        id: 'dropExchangeType',
        accessor: 'dropAmount.exchangeType',
        minWidth: 100,
        FilterComponent: FuelTypeFilter,
        getCustomFilterInfo: ({ filteredColumn }) => {
            const {
                value: { allowEmptyOrNull, values }
            } = filteredColumn;
            return new FilterBuilder()
                .withName('dropAmount.exchangeType')
                .withOperation(FilterOperation.Equals)
                .withValues(values)
                .withAllowEmptyOrNull(allowEmptyOrNull);
        },
        getFilterOptions: () => {
            const options = ExchangeTypeEnum.getOptions({
                translationKey: 'masterOrderProperties.ExchangeTypes'
            });
            return options;
        }
    },
    {
        headerTextId: 'orderMasterTable.dropCustomerId',
        id: 'dropCustomerId',
        accessor: 'dropCustomerId',
        minWidth: 100,
        infixSearchable: true
    },
    {
        headerTextId: 'orderMasterTable.dropAddressId',
        id: 'dropAddressId',
        accessor: 'dropAddressId',
        minWidth: 100,
        infixSearchable: true
    },
    {
        headerTextId: 'orderMasterTable.volume',
        id: 'volume',
        accessor: 'volume.value',
        Cell: props => {
            const { original } = props;

            const value = original.getIn(['volume', 'value']);
            if (DataUtils.isEmpty(value)) {
                return '-';
            }
            return value;
        },
        getCustomFilterInfo: ({ filteredColumn }) => {
            const { value } = filteredColumn;
            return new FilterBuilder()
                .withName('volume.value')
                .withOperation(FilterOperation.Equals)
                .withValue(value);
        }
    },
    {
        headerTextId: 'orderMasterTable.volumeUnit',
        id: 'volumeUnit',
        accessor: 'volume.unit',
        FilterComponent: FuelTypeFilter,
        Cell: ({
            original,
            tdProps: {
                rest: { t }
            }
        }) => {
            if (original.volume.unit) {
                return t(
                    `entityManagement:masterOrderProperties:volumeUnits.${original.volume.unit}`
                );
            }
            return null;
        },
        getCustomFilterInfo: ({ filteredColumn }) => {
            const {
                value: { allowEmptyOrNull, values }
            } = filteredColumn;
            return new FilterBuilder()
                .withName('volume.unit')
                .withOperation(FilterOperation.Equals)
                .withValues(values)
                .withAllowEmptyOrNull(allowEmptyOrNull);
        },
        getFilterOptions: () => {
            const options = VolumeUnitEnum.getOptions({
                translationKey: 'masterOrderProperties.volumeUnits'
            });
            return options;
        }
    },
    {
        headerTextId: 'orderMasterTable.weight',
        id: 'weight',
        accessor: 'weight.value',
        Cell: props => {
            const { original } = props;

            const value = original.getIn(['weight', 'value']);
            if (DataUtils.isEmpty(value)) {
                return '-';
            }
            return value;
        },
        minWidth: 100,
        getCustomFilterInfo: ({ filteredColumn }) => {
            const { value } = filteredColumn;
            return new FilterBuilder()
                .withName('weight.value')
                .withOperation(FilterOperation.Equals)
                .withValue(value);
        }
    },
    {
        headerTextId: 'orderMasterTable.weightUnit',
        id: 'weightUnit',
        accessor: 'weight.unit',
        FilterComponent: FuelTypeFilter,
        Cell: ({
            original,
            tdProps: {
                rest: { t }
            }
        }) => {
            if (original.weight.unit) {
                return t(
                    `entityManagement:masterOrderProperties:weightUnits.${original.weight.unit}`
                );
            }
            return null;
        },
        getCustomFilterInfo: ({ filteredColumn }) => {
            const {
                value: { allowEmptyOrNull, values }
            } = filteredColumn;
            return new FilterBuilder()
                .withName('weight.unit')
                .withOperation(FilterOperation.Equals)
                .withValues(values)
                .withAllowEmptyOrNull(allowEmptyOrNull);
        },
        getFilterOptions: () => {
            const options = WeightUnitEnum.getOptions({
                translationKey: 'masterOrderProperties.weightUnits'
            });
            return options;
        }
    },
    {
        headerTextId: 'orderMasterTable.orderedOn',
        id: 'orderedOn',
        accessor: 'orderedOn',
        dataType: CellDataTypes.DateTime,
        minWidth: 150,
        FilterComponent: DateTimeRangeFilter,
        getExtraFilterProps: () => ({
            id: 'orderedOn',
            showTime: true
        }),
        getCustomFilterInfo: ({ filteredColumn }) => {
            const {
                value: { min, max }
            } = filteredColumn;
            const rangeFilter = [];
            if (min) {
                rangeFilter.push(
                    new FilterBuilder()
                        .withName('orderedOn')
                        .withOperation(FilterOperation.GreaterThanOrEqualTo)
                        .withValue(min)
                );
            }
            if (max) {
                rangeFilter.push(
                    new FilterBuilder()
                        .withName('orderedOn')
                        .withOperation(FilterOperation.LesserThanOrEqualTo)
                        .withValue(max)
                );
            }
            return rangeFilter;
        }
    },
    {
        headerTextId: 'orderMasterTable.batchId',
        id: 'batchId',
        accessor: 'batchId',
        minWidth: 100,
        Cell: props => {
            const { original: order } = props;
            const { batchId, clientId } = order;
            return (
                <Link
                    className={classnames(theme.cellText, theme.link)}
                    to={`/client/${clientId}/batch/${batchId}`}
                >
                    {batchId}
                </Link>
            );
        }
    },
    {
        headerTextId: 'orderMasterTable.planId',
        id: 'planId',
        accessor: 'planId',
        minWidth: 100,
        Cell: props => {
            const { original: order } = props;
            const { batchId, clientId, planId } = order;
            return (
                <Link
                    className={classnames(theme.cellText, theme.link)}
                    to={`/client/${clientId}/batch/${batchId}/plan/${planId}`}
                >
                    {planId}
                </Link>
            );
        }
    },
    {
        headerTextId: 'orderMasterTable.tourId',
        id: 'tourId',
        accessor: 'tourId',
        minWidth: 100
    },
    {
        headerTextId: 'orderMasterTable.sequence',
        id: 'sequence',
        accessor: 'sequence',
        minWidth: 100
    },
    {
        headerTextId: 'orderMasterTable.userId',
        id: 'userId',
        accessor: 'userId',
        minWidth: 100
    },
    {
        headerTextId: 'orderMasterTable.homebaseId',
        id: 'homebaseId',
        accessor: 'homebaseId',
        minWidth: 100
    },
    {
        headerTextId: 'orderMasterTable.updatedBy',
        id: 'updatedBy',
        sortable: false,
        filterable: false,
        fieldId: 'auditMetadata.updatedBy',
        Cell: props => {
            const {
                original: order,
                tdProps: {
                    rest: { isGlobalPersonnel }
                }
            } = props;
            const updatedBy = VisitUtils.formatIDs(
                order?.auditMetadata?.updatedBy?.id,
                !isGlobalPersonnel
            );
            return (
                <span title={updatedBy} className={theme.cellText}>
                    {updatedBy}
                </span>
            );
        }
    },
    {
        headerTextId: 'orderMasterTable.updatedOn',
        accessor: 'auditMetadata.updatedOn',
        id: 'updatedOn',
        sortable: false,
        filterable: false,
        dataType: CellDataTypes.DateTime
    },
    {
        headerTextId: 'orderMasterTable.createdBy',
        id: 'createdBy',
        sortable: false,
        filterable: false,
        fieldId: 'auditMetadata.createdBy',
        Cell: props => {
            const {
                original: order,
                tdProps: {
                    rest: { isGlobalPersonnel }
                }
            } = props;
            const createdBy = VisitUtils.formatIDs(
                order?.auditMetadata?.createdBy?.id,
                !isGlobalPersonnel
            );
            return (
                <span title={createdBy} className={theme.cellText}>
                    {createdBy}
                </span>
            );
        }
    },
    {
        headerTextId: 'orderMasterTable.createdOn',
        accessor: 'auditMetadata.createdOn',
        id: 'createdOn',
        sortable: false,
        filterable: false,
        dataType: CellDataTypes.DateTime
    }
];

type Params = {
    clientId: string
};

type OrderIdBody = {
    entityIds: Array<string>
};

type Props = {
    clientId: string,
    teams: Map<string, Team>,
    fetchEntities: ({ clientId: string }, Object, Object) => void,
    clearData: () => void,
    updateOrder: ({
        params: Params,
        body: Object,
        totalCount: number
    }) => Promise<any>,
    validateOrderIds: ({
        params: Params,
        body: OrderIdBody
    }) => Promise<any>,
    selectedRows: Array<string>,
    data: ?OrdersDataType,
    entityIdToEdit: ?string,
    isLoading: boolean,
    location: Location,
    history: RouterHistory,
    theme: Object,
    t: (string, ?Object) => string,
    orderManagementSettings: ?OrderManagementSettings,
    tableSettings: ?Array<Object>,
    showSuccessToast: ($Shape<ToastType>) => void,
    showErrorToast: ($Shape<ToastType>) => void,
    saveTableSettings: (string, Array<Object>) => void,
    toggleRowSelection: string => void,
    isGlobalPersonnel: boolean,
    visibleSections: SectionVisibilitySettings
};

type State = {
    prevFetchEntitiesParams: Object,
    isBatchCreationDialogOpen: boolean,
    isCreateBatchInProgress: boolean,
    errorIds: Array<string>
};

const OrderMaster = entityMasterFactory({
    EntityExportContainer: MasterExportContainer,
    COLUMNS,
    EXPORT_HANDLER: new OrderMasterExportHandler(),
    options: {
        entityTableId: 'OrderMaster'
    }
});

class OrderMasterContainer extends React.PureComponent<Props, State> {
    constructor(props) {
        super(props);
        this.state = {
            prevFetchEntitiesParams: {},
            isBatchCreationDialogOpen: false,
            isCreateBatchInProgress: false,
            errorIds: []
        };
    }

    componentDidMount = () => {
        this.fetchOtherResources();
    };

    fetchOtherResources = () => {
        const { clientId, fetchOrders } = this.props;
        const filters = [];
        if (clientId) {
            fetchOrders({ clientId }, { filters });
        }
    };

    getTdProps = (state, rowInfo) => {
        if (!rowInfo) {
            return {};
        }
        const { original } = rowInfo;
        const { id } = original;
        const {
            teams,
            selectedRows,
            toggleRowSelection,
            t,
            theme,
            isGlobalPersonnel
        } = this.props;
        const isChecked = selectedRows.includes(id);
        return {
            onCheckboxClick: () => {
                toggleRowSelection(id);
            },
            isChecked,
            className: classnames({ [theme.selectedRow]: isChecked }),
            theme,
            t,
            teams,
            isGlobalPersonnel
        };
    };

    getAdditionalTableProps = () => {
        const { teams } = this.props;
        return {
            teams
        };
    };

    fetchEntities = (params, body, options) => {
        const { fetchEntities } = this.props;
        this.setState({
            prevFetchEntitiesParams: {
                params,
                body,
                options
            }
        });
        fetchEntities(params, body, options);
    };
    handleCreateBatch = (
        tasksDate: string,
        batchId: string,
        teamId: string,
        homebaseId: string,
        homebaseOptions: array<string>,
        enableCrossDock: boolean,
        commaSeparatedOrderIds: string
    ): void => {
        this.setState({ isCreateBatchInProgress: true });
        const {
            createBatch,
            allFilteredEntitiesSelected,
            selectedRows,
            clientId
        } = this.props;
        const orderSelectRequest = {};
        if (allFilteredEntitiesSelected) {
            const { filters } = this.masterContainer.prevRequestParams;
            orderSelectRequest.filters = new FilterListBuilder()
                .addAll(filters)
                .build();
        } else if (!DataUtils.isEmpty(commaSeparatedOrderIds)) {
            orderSelectRequest.selectedIds = commaSeparatedOrderIds
                .split(',')
                .map(id => id.trim())
                .filter(id => id);
        } else {
            orderSelectRequest.selectedIds = selectedRows;
        }
        const body = {
            batchId,
            tasksDate,
            teamId,
            homebaseId,
            orderSelectRequest,
            homebaseOptions,
            enableCrossDock,
            type: 'SPMD'
        };

        createBatch({
            params: { clientId },
            body
        })
            .then(() => {
                this.toggleBatchCreateView();
                this.masterContainer?.reloadData();
            })
            .finally(() => {
                this.setState({ isCreateBatchInProgress: false });
            });
    };

    toggleBatchCreateView = () => {
        this.setState(prevState => ({
            isBatchCreationDialogOpen: !prevState.isBatchCreationDialogOpen,
            errorIds: []
        }));
    };
    getMasterContainerRef = ref => {
        this.masterContainer = ref;
    };

    handleOrderUpdate = filteredEntriesCount => {
        const {
            updateOrder,
            allFilteredEntitiesSelected,
            selectedRows,
            clientId
        } = this.props;
        let totalCount;
        const orderSelectRequest = {};
        if (allFilteredEntitiesSelected) {
            const { filters } = this.masterContainer.prevRequestParams;
            orderSelectRequest.filters = new FilterListBuilder()
                .addAll(filters)
                .build();
            totalCount = filteredEntriesCount;
        } else {
            orderSelectRequest.selectedIds = selectedRows;
            totalCount = selectedRows.length;
        }
        const body = {
            orderStatus: OrderSubStatusEnum.QuoteRequested,
            orderSelectRequest
        };

        updateOrder({
            params: { clientId },
            body,
            totalCount
        }).then(() => {
            this.masterContainer?.reloadData();
        });
    };

    validateOrderIds = (orderIds: Array<string>) => {
        const { validateOrderIds, clientId } = this.props;
        const body = {
            entityIds: orderIds
        };
        validateOrderIds({
            params: { clientId },
            body
        }).then(data => {
            if (data) {
                this.setState({ errorIds: data.availableIds });
            }
        });
    };

    render() {
        const {
            t,
            clientId,
            theme,
            orderManagementSettings,
            selectedRows,
            visibleSections,
            allFilteredEntitiesSelected,
            data
        } = this.props;
        const {
            isBatchCreationDialogOpen,
            isCreateBatchInProgress,
            errorIds
        } = this.state;
        const customActions = {
            onCreateBatch: this.toggleBatchCreateView,
            onUpdateOrder:
                visibleSections?.showQuoteManagementSection &&
                selectedRows.length > 0
                    ? this.handleOrderUpdate
                    : null
        };
        return (
            <div className={theme.wrap}>
                <OrderMaster
                    {...this.props}
                    shouldExportInactiveEntities
                    customActions={customActions}
                    fetchEntities={this.fetchEntities}
                    emptyStateImageSrc="/img/masterHomebase-empty.svg"
                    entityNameId={ENTITY_NAME_ID}
                    entityExportId={ENTITY_EXPORT_ID}
                    entityId="orderId"
                    entitySettings={orderManagementSettings}
                    entitySettingsName="orderMasterSettings"
                    fetchOptions={{ clientId }}
                    getTdProps={this.getTdProps}
                    getAdditionalTableProps={this.getAdditionalTableProps}
                    hasFreezableColumns
                    frozenColumnLimit={FROZEN_COLUMN_LIMIT}
                    pinTooltip={t('pinTooltip')}
                    unpinTooltip={t('unpinTooltip')}
                    isEditEntityActive={false}
                    refProvider={this.getMasterContainerRef}
                    shouldHideAddEntity
                    shouldHideEditEntity
                    shouldHideDeleteEntity
                    enableFilteredEntitiesSelection
                />
                {isBatchCreationDialogOpen ? (
                    <CreateBatchModal
                        onCancel={this.toggleBatchCreateView}
                        onCreate={this.handleCreateBatch}
                        clientId={clientId}
                        isCreateBatchInProgress={isCreateBatchInProgress}
                        isNoRowSelected={DataUtils.isEmpty(selectedRows)}
                        totalOrders={
                            allFilteredEntitiesSelected
                                ? data.paginationInfo.total
                                : selectedRows.length
                        }
                        validateOrderIds={this.validateOrderIds}
                        errorIds={errorIds}
                    />
                ) : null}
            </div>
        );
    }
}

const mapStateToProps = (state, ownProps) => {
    const clientId = clientSelector(state, ownProps);
    const isLoading = OrderMasterSelectors.isEntitiesLoading(state, ownProps);
    const data = OrderMasterSelectors.getEntities(state, ownProps);
    const teams = teamsSelector(state, ownProps);
    const orderManagementSettings = getOrderManagementSettings(state, ownProps);
    const tableSettings = getEntityTablePreferences(state, ENTITY_TABLE_ID);
    const isGlobalPersonnel = isGlobalPersonnelSelector(state, ownProps);
    const isExportEntityActive = isExportEntityActiveSelector(state, ownProps);
    const visibleSections = getSectionVisibilitySettings(state, ownProps);

    return {
        clientId,
        teams,
        isLoading,
        data,
        orderManagementSettings,
        tableSettings,
        isGlobalPersonnel,
        isExportEntityActive,
        visibleSections
    };
};

const withRedux = connect(mapStateToProps, {
    fetchEntities: OrderActions.fetchEntities,
    fetchOrders: OrderActions.fetchOrders,
    clearData: OrderActions.clearData,
    createBatch: OrderActions.createBatch,
    updateOrder: OrderActions.updateOrder,
    showToastAction: ToastActions.showToast,
    updateToastAction: ToastActions.updateToast,
    hideToastAction: ToastActions.hideToast,
    showSuccessToast: ToastActions.showSuccessToast,
    showErrorToast: ToastActions.showErrorToast,
    saveTableSettings: AppPreferencesActions.saveEntityTablePreferences,
    validateOrderIds: OrderActions.validateOrderIds
});

const enhance = compose(
    themr(null, theme),
    withRouter,
    withRedux,
    translate('entityManagement'),
    MasterContainerHOC
);

const ThemedOrderMasterContainer = enhance(OrderMasterContainer);

export default ThemedOrderMasterContainer;
